import { type MouseEvent, type ReactNode, useEffect } from 'react'

import { Button } from 'slab'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  primaryAction?: {
    label: string
    handleOnClick: () => void
    disabled?: boolean
  }
  secondaryAction?: {
    label: string
    handleOnClick: () => void
  }
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
}

export function Modal ({
  isOpen,
  onClose,
  title,
  children,
  primaryAction,
  secondaryAction,
  size = 'md',
}: ModalProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && primaryAction && !primaryAction.disabled) {
        primaryAction.handleOnClick()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, primaryAction, onClose])

  if (!isOpen) {
    return null
  }

  const modalTitleId = title ? 'modal-title' : undefined
  const modalDescriptionId = 'modal-description'

  const handleContentClick = (e: MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-gray-800/40 bg-opacity-40 p-4'
      onClick={onClose}
      role='dialog'
      aria-modal='true'
      aria-labelledby={modalTitleId}
      aria-describedby={modalDescriptionId}
    >
      <div
        className={`bg-white rounded-lg shadow-xl w-full ${SIZES[size]} overflow-hidden flex flex-col`}
        onClick={handleContentClick}
      >
        <div className={`flex flex-col flex-grow px-6 pt-8 pb-3 ${title && children ? 'gap-3' : ''}`}>
          {title && (
            <div className='flex items-center justify-between'>
              <h3 id={modalTitleId} className='text-xl font-semibold text-gray-800'>
                {title}
              </h3>
            </div>
          )}
          <div className='flex-grow' id={modalDescriptionId}>
            {children}
          </div>
        </div>

        {(primaryAction || secondaryAction) && (
          <div className='flex justify-end gap-2 py-4 px-6 border-t border-gray-200'>
            {secondaryAction && (
              <Button size='large' variant='secondary' onClick={secondaryAction.handleOnClick}>
                {secondaryAction.label}
              </Button>
            )}
            {primaryAction && (
              <Button size='large' variant='primary' onClick={primaryAction.handleOnClick} disabled={primaryAction.disabled}>
                {primaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
